import React from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../Datas/productData";
import PolicyNav, { POLICY_UPDATED } from "./PolicyNav";
import "../css/privacy.scss";

function Privacy() {
  const { contact } = siteConfig;

  return (
    <main className="policy-page privacy">
      <header className="policy-hero policy-hero--legal">
        <p className="policy-eyebrow">Legal</p>
        <h1>Privacy policy</h1>
        <p>
          What information {siteConfig.brandName} collects, why we use it, who
          receives it, and the choices available to you.
        </p>
        <span>Effective {POLICY_UPDATED}</span>
      </header>

      <div className="policy-layout">
        <PolicyNav current="/privacy" />

        <article className="policy-content policy-content--legal">
          <section className="policy-intro" aria-labelledby="privacy-overview">
            <p className="policy-eyebrow">Your information</p>
            <h2 id="privacy-overview">Privacy in plain language.</h2>
            <p>
              {siteConfig.brandName} is operated by {contact.legalName}. This
              policy applies when you visit this website, place an order,
              contact us, or otherwise interact with our online store. It does
              not control an independent third party’s website or service.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="privacy-collect">
            <div className="policy-section__heading">
              <span>01</span>
              <h2 id="privacy-collect">Information we collect</h2>
            </div>
            <ul>
              <li>
                <strong>Identifiers and contact details:</strong> name, email,
                delivery and billing address, phone number if requested, and
                account or order identifiers.
              </li>
              <li>
                <strong>Order and commercial information:</strong> products,
                variants, amounts, discounts, delivery status, returns, refunds,
                and customer service history.
              </li>
              <li>
                <strong>Payment information:</strong> payment status and limited
                transaction details. Card numbers and security codes are entered
                with and processed by the hosted payment provider, not stored by
                this storefront.
              </li>
              <li>
                <strong>Communications:</strong> messages, attachments, survey
                responses, reviews, and other information you choose to send.
              </li>
              <li>
                <strong>Device and activity data:</strong> IP address, browser,
                device type, referring pages, timestamps, diagnostics, and
                interactions that our hosting and security providers may log.
              </li>
              <li>
                <strong>Browser-stored preferences:</strong> bag contents, saved
                items, announcement preference, and temporary order confirmation.
              </li>
            </ul>
          </section>

          <section className="policy-section" aria-labelledby="privacy-sources">
            <div className="policy-section__heading">
              <span>02</span>
              <h2 id="privacy-sources">Where information comes from</h2>
            </div>
            <p>
              We receive information directly from you, automatically from your
              device and browser, and from service providers that help process
              payments, prevent fraud, host the store, deliver parcels, and
              support orders. If you buy on behalf of another recipient, you
              must have permission to provide their delivery details.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="privacy-use">
            <div className="policy-section__heading">
              <span>03</span>
              <h2 id="privacy-use">How and why we use information</h2>
            </div>
            <ul>
              <li>to take payment, fulfill, deliver, and administer orders;</li>
              <li>to provide tracking, returns, refunds, and customer support;</li>
              <li>to operate, secure, troubleshoot, and improve the store;</li>
              <li>to detect fraud, abuse, security incidents, and policy violations;</li>
              <li>to maintain business, tax, accounting, and compliance records;</li>
              <li>to establish, exercise, or defend legal claims; and</li>
              <li>to send marketing only where permitted, with an opt-out in each message.</li>
            </ul>
            <p>
              Where a law requires a legal basis, we rely as appropriate on
              performance of a contract, legitimate interests, compliance with
              law, or consent. You may withdraw consent at any time, without
              affecting processing already completed.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="privacy-disclose">
            <div className="policy-section__heading">
              <span>04</span>
              <h2 id="privacy-disclose">When information is disclosed</h2>
            </div>
            <p>
              We disclose only what is reasonably needed to providers of website
              hosting, payment processing, fraud prevention, delivery, email,
              customer support, professional advice, and data storage. We may
              also disclose information to comply with law, protect people or
              rights, investigate misuse, or complete a merger, financing,
              reorganization, or sale subject to appropriate protections.
            </p>
            <p>
              We do not sell personal information for money. We do not currently
              share personal information for cross-context behavioral advertising
              or use sensitive personal information to infer characteristics.
              If those practices change, we will update this policy and provide
              any legally required choices before the change applies.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="privacy-cookies">
            <div className="policy-section__heading">
              <span>05</span>
              <h2 id="privacy-cookies">Cookies &amp; browser storage</h2>
            </div>
            <p>
              This storefront uses essential local and session storage to keep
              requested features working. We do not currently place advertising
              cookies. See the <Link to="/cookies">Cookie Policy</Link> for the
              current list of purposes and browser controls.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="privacy-retention">
            <div className="policy-section__heading">
              <span>06</span>
              <h2 id="privacy-retention">Retention &amp; security</h2>
            </div>
            <p>
              We keep personal information only as long as reasonably necessary
              for the purposes described above. Order, tax, and accounting
              records may generally be kept for up to seven years where needed;
              support and security records are kept for a period proportionate
              to the issue. Browser-stored data remains until you clear it or
              the applicable browser session ends.
            </p>
            <p>
              We use administrative, technical, and organizational safeguards
              appropriate to the nature of the information. No internet service
              can guarantee absolute security, so please use care when sending
              information online.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="privacy-transfers">
            <div className="policy-section__heading">
              <span>07</span>
              <h2 id="privacy-transfers">International data transfers</h2>
            </div>
            <p>
              We are based in the United States and may use providers in other
              countries. Your information may therefore be processed outside
              your place of residence. Where required, we use contractual or
              other recognized safeguards for international transfers.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="privacy-rights">
            <div className="policy-section__heading">
              <span>08</span>
              <h2 id="privacy-rights">Your privacy rights</h2>
            </div>
            <p>
              Depending on your location and subject to legal exceptions, you
              may request access, correction, deletion, restriction, portability,
              or an explanation of how we use and disclose your information. You
              may object to certain processing, withdraw consent, opt out of
              marketing, or appeal a denied request where local law provides it.
              You will not be discriminated against for exercising a privacy right.
            </p>
            <p>
              California residents may also have rights to know the categories,
              sources, purposes, and recipients of personal information; correct
              or delete information; opt out of sale or sharing; limit certain
              uses of sensitive information; and use an authorized agent. We do
              not currently engage in sale or sharing that requires an opt-out.
            </p>
            <p>
              Submit a request by emailing {contact.email}. We may ask for
              information reasonably necessary to verify your identity and
              authority. You may also complain to the privacy regulator in your
              jurisdiction.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="privacy-children">
            <div className="policy-section__heading">
              <span>09</span>
              <h2 id="privacy-children">Children’s privacy</h2>
            </div>
            <p>
              The store is intended for a general adult audience and is not
              directed to children under 13. We do not knowingly collect personal
              information from children under 13. If you believe a child has
              provided information, contact us so we can review and delete it as
              appropriate.
            </p>
          </section>

          <section className="policy-section" aria-labelledby="privacy-updates">
            <div className="policy-section__heading">
              <span>10</span>
              <h2 id="privacy-updates">Updates &amp; contact</h2>
            </div>
            <p>
              We may revise this policy as the store or law changes. The date at
              the top shows the current version; material changes will receive
              additional notice when required.
            </p>
            <p>
              Privacy contact: {contact.legalName}, EIN {contact.ein},
              {` ${contact.address}`}; email {contact.email}.
            </p>
          </section>

          <section className="policy-help" aria-labelledby="privacy-help">
            <div>
              <p className="policy-eyebrow">Privacy questions</p>
              <h2 id="privacy-help">Ask about your information.</h2>
            </div>
            <a href={`mailto:${contact.email}`}>
              {contact.email} <span aria-hidden="true">↗</span>
            </a>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Privacy;
